import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import { emailCampaignsApi } from "./api/emailCampaignsApi";

export default function SeasonalCampaignPanel({ onLaunch }) {
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    emailCampaignsApi
      .seasonalOccasions()
      .then(setOccasions)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || occasions.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Seasonal Opportunities
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 1,
          "&::-webkit-scrollbar": { height: 4 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 2, bgcolor: "divider" },
        }}
      >
        {occasions.map((occasion) => (
          <OccasionCard key={occasion.key} occasion={occasion} onLaunch={onLaunch} />
        ))}
      </Box>
    </Box>
  );
}

function OccasionCard({ occasion, onLaunch }) {
  const isImminent  = occasion.days_until >= 0 && occasion.days_until <= 7;
  const isToday     = occasion.days_until === 0;
  const isPast      = occasion.days_until < 0;

  const daysLabel = isPast
    ? `${Math.abs(occasion.days_until)}d ago`
    : isToday
    ? "Today!"
    : `in ${occasion.days_until} day${occasion.days_until !== 1 ? "s" : ""}`;

  return (
    <Paper
      elevation={2}
      sx={{
        minWidth: 220,
        maxWidth: 240,
        p: 2,
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flexShrink: 0,
        border: isImminent ? "2px solid" : "1px solid",
        borderColor: isImminent ? "warning.main" : "divider",
        position: "relative",
      }}
    >
      {isImminent && (
        <Chip
          label="Upcoming!"
          color="warning"
          size="small"
          sx={{ position: "absolute", top: -10, right: 12, fontWeight: 700 }}
        />
      )}

      <Typography variant="h4" sx={{ lineHeight: 1 }}>{occasion.emoji}</Typography>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} noWrap>{occasion.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(occasion.date + "T00:00:00").toLocaleDateString("en-GB", {
            day: "numeric", month: "short", year: "numeric",
          })}
        </Typography>
      </Box>

      <Chip
        label={daysLabel}
        size="small"
        color={isPast ? "default" : isToday ? "error" : "primary"}
        variant="outlined"
        sx={{ width: "fit-content", fontSize: "0.7rem" }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, lineClamp: 2 }}>
        {occasion.description}
      </Typography>

      <Button
        variant="contained"
        size="small"
        fullWidth
        onClick={() => onLaunch(occasion)}
        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, mt: "auto" }}
      >
        Launch Campaign
      </Button>
    </Paper>
  );
}
